import { useCallback, useMemo, useRef, useState } from "react";
import useToast from "../../../hooks/useToast";
import { useAuth2fa } from "../../../pages/Login/store/useAuth2fa";
import { _api_post_generate_otp, _api_post_verify_mfa } from "../api";
import { useMutation } from "react-query";
import { useAuthDetailStore, useAuthStore } from "../../../stores/useAuthStore";
import { ROUTES } from "../../../constants/routes";
import { useNavigate } from "react-router-dom";

const COUNTDOWN_LIMIT_SECONDS = 120; // 120 secondes

const useOTP = () => {
    // * STATES
    // * STATES
    // * STATES
    const inputRefs = useRef([]);
    const navigate = useNavigate();

    const [otp, setOtp] = useState(Array(6).fill(""));
    const [timer, setTimer] = useState(null); // timer en secondes
    const [error, setError] = useState(false); // error OTP
    const [errorMessage, setErrorMessage] = useState(false); // error Message OTP
    const [toast, setToast] = useToast();

    // * STORES
    // * STORES
    // * STORES
    const store_2fa_email = useAuth2fa((state) => state.email);
    const store_2fa_clear = useAuth2fa((state) => state.clear);
    const setUser = useAuthStore((state) => state.setUser);
    const setDetailUser = useAuthDetailStore((state) => state.setDetailUser);

    // * QUERIES & MUTATIONS
    // * QUERIES & MUTATIONS
    // * QUERIES & MUTATIONS
    const { isLoading: is_verifying_otp, mutate: mutate_verify_otp } =
        useMutation(
            (data) => {
                return _api_post_verify_mfa({
                    username: data.username,
                    otp: data.otp,
                });
            },
            {
                onSuccess: (data) => {
                    if (data?.success) {
                        setUser({
                            access: data.access_token,
                        });
                        setDetailUser(data.user);
                        store_2fa_clear();
                        navigate(ROUTES.home);
                        return;
                    }
                    setError(true);
                    setErrorMessage(data?.message);
                },
                onError: (err) => {
                    setError(true);
                    setErrorMessage(err);
                    console.error(err);
                },
            }
        );
    const { isLoading: is_generating_otp, mutate: mutate_generate_otp } =
        useMutation(
            (data) => {
                return _api_post_generate_otp({
                    username: data.username,
                });
            },
            {
                onSuccess: (data) => {
                    if (data?.success) {
                        // start countdown timer
                        start_countdown();
                        setToast((state) => ({
                            ...state,
                            visible: true,
                            level: "success",
                            content: "Un code vous a été envoyé par mail",
                        }));
                        return;
                    }
                    setToast((state) => ({
                        ...state,
                        visible: true,
                        level: "danger",
                        content: data?.message,
                    }));
                },
                onError: (err) => {
                    setToast((state) => ({
                        ...state,
                        visible: true,
                        level: "danger",
                        content:
                            "Une erreur est survenue lors de la génération du code",
                    }));
                    console.error(err);
                },
            }
        );

    // * FUNCTIONS
    // * FUNCTIONS
    // * FUNCTIONS
    const handleChange = (index, value) => {
        if (isNaN(Number(value))) return;

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        if (value !== "" && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
        setError(false);
    };
    const handleKeyDown = (index, e) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            setError(false);
            inputRefs.current[index - 1]?.focus();
        }
    };
    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData
            .getData("text")
            .trim()
            .replaceAll(" ", "")
            .slice(0, 6);

        if (/^\d+$/.test(pastedData)) {
            const digits = pastedData.split("");
            const newOtp = [...otp];

            digits.forEach((digit, index) => {
                if (index < 6) newOtp[index] = digit;
            });

            setOtp(newOtp);
            inputRefs.current[Math.min(5, digits.length - 1)]?.focus();
            setError(false);
        }
    };
    const start_countdown = () => {
        setTimer(0);
        let counter = 0;
        const interval_countdown = setInterval(() => {
            if (counter === COUNTDOWN_LIMIT_SECONDS) {
                counter = 0;
                setTimer(null);
                return clearInterval(interval_countdown);
            }
            counter++;
            return setTimer((timer) => timer + 1);
        }, 1000); // chaque seconde
        return;
    };

    const hashed_mail = useMemo(() => {
        if (!store_2fa_email) return "";
        const split_mail = store_2fa_email.split("@");
        const hashed = split_mail
            .at(0)
            .split("")
            .map((letter, i) => (i === 0 ? letter : "*"))
            .join("");
        return hashed + "@" + split_mail.at(1);
    }, [store_2fa_email]);

    // * return
    // * return
    // * return
    return {
        otp,
        setOtp,
        inputRefs,
        disable_btn_verify_otp:
            !otp.every((num) => parseInt(num) >= 0) || error,
        hashed_mail: hashed_mail,
        error: error,
        errorMessage: errorMessage,
        alert: {
            toast,
        },
        functions: {
            handleChange,
            handleKeyDown,
            handlePaste,
        },
        count_down: {
            seconds: timer !== null ? COUNTDOWN_LIMIT_SECONDS - timer : null,
        },
        mutations: {
            generation_otp: {
                isLoading: is_generating_otp,
                mutate: () => {
                    mutate_generate_otp({
                        username: store_2fa_email,
                    });
                    return;
                },
            },
            verify_otp: {
                isLoading: is_verifying_otp,
                mutate: () => {
                    mutate_verify_otp({
                        username: store_2fa_email,
                        otp: otp.join(""),
                    });
                    return;
                },
            },
        },
    };
};

export default useOTP;
